import { useCallback, useRef } from "react";

export type RecordingMode = "raw" | "processed";

export type RecordingState = "idle" | "recording" | "processing" | "preview" | "uploading";

// Preferred MIME types in order of preference
const WEBM_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm;codecs=vorbis",
  "audio/webm",
];

const AAC_MIME_TYPES = [
  "audio/mp4;codecs=mp4a.40.2",
  "audio/aac",
  "audio/m4a",
  "audio/mp4",
];

const MP3_MIME_TYPES = ["audio/mpeg"];

/**
 * Pick the first supported MIME type for MediaRecorder.
 */
function pickSupportedMimeType(): string | null {
  // Prefer WebM with Opus codec for best quality/size
  for (const mime of WEBM_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mime)) {
      return mime;
    }
  }
  // Fallback to AAC for Safari
  for (const mime of AAC_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mime)) {
      return mime;
    }
  }
  // Fallback to MP3
  for (const mime of MP3_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mime)) {
      return mime;
    }
  }
  return null;
}

/**
 * Check if the browser is Android Chrome (for default mode selection).
 */
function isAndroidChrome(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent;
  return /android/i.test(ua) && /chrome/i.test(ua) && !/edge/i.test(ua);
}

/**
 * Check if the browser is Brave or Safari (for PCM capture path).
 */
function isBraveOrSafari(): boolean {
  if (typeof window === "undefined") return false;
  const nav = navigator as typeof navigator & { brave?: unknown };
  return (
    !!nav.brave || /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  );
}

export interface UseAudioRecordingOptions {
  maxDurationSeconds: number;
  recordingMode: RecordingMode;
  debugMode: boolean;
  /** Called before recording starts. If returns false or throws, recording won't start. */
  onBeforeStart?: () => Promise<boolean | void>;
  /** Called when rate limit check fails */
  onRateLimitError?: (error: string) => void;
  /** Called when state changes */
  onStateChange?: (state: RecordingState) => void;
  /** Called when recording produces a blob (Chrome/fallback path) */
  onBlobRecorded?: (blob: Blob) => void;
  /** Called when recording produces PCM data (Brave/Safari path) */
  onPcmRecorded?: (pcmData: Float32Array[], sampleRate: number) => void;
  /** Called when an error occurs */
  onError?: (error: string) => void;
  /** Called when timer updates (for display) */
  onTimerUpdate?: (seconds: number) => void;
  /** Called when recording stops at time limit */
  onStoppedAtTimeLimit?: () => void;
}

export interface UseAudioRecordingReturn {
  /** Start recording */
  startRecording: () => Promise<void>;
  /** Stop recording */
  stopRecording: () => void;
  /** Stop all streams and clean up resources */
  stopStreams: () => void;
  /** Get audio constraints based on recording mode */
  getAudioConstraints: () => MediaTrackConstraints;
  /** Reference to preferred MIME type */
  preferredMimeTypeRef: React.RefObject<string | null>;
  /** Reference to check if stopped at time limit */
  stoppedAtTimeLimitRef: React.RefObject<boolean>;
  /** Get the default recording mode based on platform */
  getDefaultRecordingMode: () => RecordingMode;
}

/**
 * Hook to manage audio recording with browser-specific paths.
 * Handles Chrome (MediaRecorder + decode), Brave/Safari (PCM capture), and fallback paths.
 */
export function useAudioRecording({
  maxDurationSeconds,
  recordingMode,
  debugMode,
  onBeforeStart,
  onRateLimitError,
  onStateChange,
  onBlobRecorded,
  onPcmRecorded,
  onError,
  onTimerUpdate,
  onStoppedAtTimeLimit,
}: UseAudioRecordingOptions): UseAudioRecordingReturn {
  // Refs for recording resources
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const finalizeRef = useRef(false);
  const pcmBuffersRef = useRef<Float32Array[]>([]);
  const usePcmCaptureRef = useRef(false);
  const pcmStopHandlerRef = useRef<(() => void) | null>(null);
  const preferredMimeTypeRef = useRef<string | null>(null);
  const stoppedAtTimeLimitRef = useRef(false);

  /**
   * Get the default recording mode based on platform.
   */
  const getDefaultRecordingMode = useCallback((): RecordingMode => {
    return isAndroidChrome() ? "raw" : "processed";
  }, []);

  /**
   * Get audio constraints based on recording mode.
   */
  const getAudioConstraints = useCallback((): MediaTrackConstraints => {
    if (recordingMode === "raw") {
      return {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 1,
      };
    } else {
      return {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      };
    }
  }, [recordingMode]);

  /**
   * Stop all streams and clean up resources.
   */
  const stopStreams = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    startTimeRef.current = null;

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.stream) {
      try {
        recorder.stream.getTracks().forEach((track) => {
          if (track.readyState !== "ended") {
            track.stop();
          }
        });
      } catch (e) {
        console.warn("Error stopping stream tracks:", e);
      }
      mediaRecorderRef.current = null;
    }

    if (gainNodeRef.current) {
      try {
        gainNodeRef.current.disconnect();
      } catch {
        // Already disconnected, ignore
      }
      gainNodeRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close().catch(console.error);
      audioContextRef.current = null;
    }

    pcmBuffersRef.current = [];
    pcmStopHandlerRef.current = null;
  }, []);

  /**
   * Stop recording (triggers onstop handler).
   */
  const stopRecording = useCallback(() => {
    // Clear the timer immediately when stopping manually
    // Note: We keep startTimeRef.current so onstop handlers can calculate duration
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (pcmStopHandlerRef.current) {
      pcmStopHandlerRef.current();
      pcmStopHandlerRef.current = null;
      return;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      finalizeRef.current = false;
      recorder.stop();
    }
  }, []);

  /**
   * Set up the recording timer.
   */
  const setupTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    stoppedAtTimeLimitRef.current = false;

    timerRef.current = setInterval(() => {
      if (!startTimeRef.current) return;
      
      // Check if recording has stopped - if so, don't continue timer updates
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state !== "recording") {
        return;
      }
      
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const displayTime = Math.min(elapsed, maxDurationSeconds);
      onTimerUpdate?.(displayTime);

      // Request final data chunk when timer hits max duration
      if (elapsed >= maxDurationSeconds && elapsed < maxDurationSeconds + 0.1) {
        if (recorder.state === "recording") {
          recorder.requestData();
        }
      }

      // Stop recording after buffer (2.5 seconds) to ensure we capture full duration
      // Only trigger if recording is still active (not manually stopped)
      if (elapsed >= maxDurationSeconds + 2.5) {
        if (recorder.state === "recording") {
          stoppedAtTimeLimitRef.current = true;
          onStoppedAtTimeLimit?.();
          stopRecording();
        }
      }
    }, 200);
  }, [maxDurationSeconds, onStoppedAtTimeLimit, onTimerUpdate, stopRecording]);

  /**
   * Handle Chrome recording path (MediaRecorder with PCM decoding).
   */
  const handleChromeRecording = useCallback(
    (stream: MediaStream) => {
      const preferredMime = pickSupportedMimeType();
      preferredMimeTypeRef.current = preferredMime;

      const recorder = preferredMime
        ? new MediaRecorder(stream, {
            mimeType: preferredMime,
            audioBitsPerSecond: 128000,
          })
        : new MediaRecorder(stream, { audioBitsPerSecond: 128000 });
      mediaRecorderRef.current = recorder;

      const audioChunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      recorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        onError?.("Recording error occurred. Please try again.");
        stopRecording();
      };

      recorder.onstop = async () => {
        // Clear timer if still running
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        const finalBlob = new Blob(audioChunks, {
          type: recorder.mimeType || preferredMime || "audio/webm",
        });

        const recordingDuration = startTimeRef.current
          ? (Date.now() - startTimeRef.current) / 1000
          : 0;
        
        // Clear startTimeRef after calculating duration
        startTimeRef.current = null;

        if (finalBlob.size < 1000 || recordingDuration < 0.5) {
          console.warn("MediaRecorder stopped immediately:", {
            blobSize: finalBlob.size,
            duration: recordingDuration,
            chunks: audioChunks.length,
          });

          if (recordingDuration < 0.5) {
            onError?.(
              "Recording stopped unexpectedly. This may happen after network changes. Please try again."
            );
            onStateChange?.("idle");
            stopStreams();
            return;
          }

          onBlobRecorded?.(finalBlob);
          return;
        }

        // Try to decode and get PCM for better quality encoding
        try {
          const AudioContextCtor =
            window.AudioContext ||
            (
              window as typeof window & {
                webkitAudioContext?: typeof AudioContext;
              }
            ).webkitAudioContext;
          if (!AudioContextCtor) {
            throw new Error("AudioContext not available");
          }

          const decodeContext = new AudioContextCtor();
          const arrayBuffer = await finalBlob.arrayBuffer();
          let audioBuffer = await decodeContext.decodeAudioData(
            arrayBuffer.slice(0)
          );
          await decodeContext.close();

          // Trim AudioBuffer to exactly maxDurationSeconds
          const maxSamples = Math.floor(
            audioBuffer.sampleRate * maxDurationSeconds
          );
          if (audioBuffer.length > maxSamples) {
            const trimmedBuffer = decodeContext.createBuffer(
              audioBuffer.numberOfChannels,
              maxSamples,
              audioBuffer.sampleRate
            );
            for (
              let channel = 0;
              channel < audioBuffer.numberOfChannels;
              channel++
            ) {
              const channelData = audioBuffer.getChannelData(channel);
              trimmedBuffer.copyToChannel(
                channelData.subarray(0, maxSamples),
                channel
              );
            }
            audioBuffer = trimmedBuffer;
          }

          const sampleRate = audioBuffer.sampleRate;
          const numChannels = audioBuffer.numberOfChannels;
          const pcmData: Float32Array[] = [];
          let maxAmplitude = 0;

          for (let channel = 0; channel < numChannels; channel++) {
            const channelData = audioBuffer.getChannelData(channel);
            pcmData.push(channelData);

            for (let i = 0; i < channelData.length; i++) {
              const abs = Math.abs(channelData[i]);
              if (abs > maxAmplitude) {
                maxAmplitude = abs;
              }
            }
          }

          if (debugMode) {
            console.log("PCM from MediaRecorder decoded:", {
              sampleRate,
              numChannels: pcmData.length,
              samplesPerChannel: pcmData[0]?.length || 0,
              durationSeconds: pcmData[0] ? pcmData[0].length / sampleRate : 0,
              maxAmplitude: maxAmplitude.toFixed(4),
              blobSize: finalBlob.size,
            });
          }

          if (
            pcmData.length > 0 &&
            pcmData[0].length > 0 &&
            maxAmplitude > 0.001
          ) {
            onPcmRecorded?.(pcmData, sampleRate);
          } else {
            console.warn("PCM data is empty or silent, using blob directly");
            onBlobRecorded?.(finalBlob);
          }
        } catch (err) {
          console.error("Error decoding final audio blob:", err);
          onBlobRecorded?.(finalBlob);
        }
      };

      recorder.start(100);

      if (recorder.state !== "recording") {
        console.error("MediaRecorder failed to start, state:", recorder.state);
        onError?.("Failed to start recording. Please try again.");
        stopStreams();
        onStateChange?.("idle");
        return false;
      }

      if (debugMode) {
        console.log(
          "MediaRecorder started successfully, state:",
          recorder.state
        );
      }
      return true;
    },
    [
      debugMode,
      maxDurationSeconds,
      onBlobRecorded,
      onError,
      onPcmRecorded,
      onStateChange,
      stopRecording,
      stopStreams,
    ]
  );

  /**
   * Handle Brave/Safari recording path (PCM capture via ScriptProcessorNode).
   */
  const handleBraveSafariRecording = useCallback(
    async (stream: MediaStream) => {
      usePcmCaptureRef.current = true;
      pcmStopHandlerRef.current = null;

      const AudioContextCtor =
        window.AudioContext ||
        (window as typeof window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioContextCtor) {
        throw new Error("Web Audio API not supported");
      }

      const audioContext = new AudioContextCtor();
      audioContextRef.current = audioContext;

      if (debugMode) {
        console.log(
          "AudioContext initial state:",
          audioContext.state,
          "sampleRate:",
          audioContext.sampleRate
        );
      }

      if (audioContext.state === "suspended") {
        await audioContext.resume();
        if (debugMode) {
          console.log("AudioContext resumed, new state:", audioContext.state);
        }
      }

      // Wait a bit for context to be ready
      if (audioContext.state !== "running") {
        await new Promise((resolve) => setTimeout(resolve, 100));
        if (audioContext.state === "suspended") {
          await audioContext.resume();
        }
        if (debugMode) {
          console.log("AudioContext state after wait:", audioContext.state);
        }
      }

      const source = audioContext.createMediaStreamSource(stream);
      if (debugMode) {
        console.log("MediaStreamSource created:", {
          channelCount: source.channelCount,
          channelCountMode: source.channelCountMode,
          numberOfInputs: source.numberOfInputs,
          numberOfOutputs: source.numberOfOutputs,
        });
      }

      const numChannels = Math.min(source.channelCount || 1, 2);
      const sampleRate = audioContext.sampleRate;
      const bufferSize = 4096;
      const pcmBuffers: Float32Array[] = Array.from(
        { length: numChannels },
        () => new Float32Array(0)
      );
      let maxAmplitude = 0;
      let captureCount = 0;
      let isCapturing = true;

      if (debugMode) {
        console.log(
          `Creating ScriptProcessorNode: bufferSize=${bufferSize}, inputChannels=${numChannels}, outputChannels=${numChannels}`
        );
      }

      // Use ScriptProcessorNode for direct PCM capture
      const scriptProcessor = audioContext.createScriptProcessor(
        bufferSize,
        numChannels,
        numChannels
      );

      scriptProcessor.onaudioprocess = (event) => {
        if (!isCapturing) return;

        if (captureCount === 0 && debugMode) {
          console.log("ScriptProcessorNode callback fired for the first time!");
        }

        const inputBuffer = event.inputBuffer;
        const actualChannels = inputBuffer.numberOfChannels;

        for (
          let channel = 0;
          channel < Math.min(actualChannels, numChannels);
          channel++
        ) {
          const inputData = inputBuffer.getChannelData(channel);
          const current = pcmBuffers[channel];
          const newLength = current.length + inputData.length;
          const merged = new Float32Array(newLength);
          merged.set(current);
          merged.set(inputData, current.length);
          pcmBuffers[channel] = merged;

          // Check for audio
          let max = 0;
          let sum = 0;
          for (let i = 0; i < inputData.length; i++) {
            const abs = Math.abs(inputData[i]);
            sum += abs;
            if (abs > max) max = abs;
          }
          const avg = sum / inputData.length;
          if (max > maxAmplitude) {
            maxAmplitude = max;
          }
          if (debugMode && (max > 0.01 || avg > 0.001)) {
            console.log(
              `PCM audio detected - channel ${channel}, max: ${max.toFixed(4)}, avg: ${avg.toFixed(4)}`
            );
          }
        }

        captureCount++;
        if (debugMode && captureCount % 50 === 0) {
          console.log(
            `PCM capture: ${captureCount} buffers, ${pcmBuffers[0]?.length || 0} samples, max amplitude: ${maxAmplitude.toFixed(4)}`
          );
        }
      };

      // Connect to keep the node active (gain 0 to prevent feedback)
      const silentGain = audioContext.createGain();
      silentGain.gain.value = 0;
      source.connect(scriptProcessor);
      scriptProcessor.connect(silentGain);
      silentGain.connect(audioContext.destination);

      const preferredMime = pickSupportedMimeType();
      preferredMimeTypeRef.current = preferredMime;

      const recorder = preferredMime
        ? new MediaRecorder(stream, {
            mimeType: preferredMime,
            audioBitsPerSecond: 128000,
          })
        : new MediaRecorder(stream, { audioBitsPerSecond: 128000 });

      recorder.onerror = (event) => {
        console.error("MediaRecorder error (Brave/Safari):", event);
        onError?.("Recording error occurred. Please try again.");
        stopRecording();
      };

      mediaRecorderRef.current = recorder;

      const audioChunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        // Stop capturing
        isCapturing = false;

        // Disconnect scriptProcessor
        scriptProcessor.disconnect();
        scriptProcessor.onaudioprocess = null;

        if (audioContextRef.current) {
          audioContextRef.current.close().catch(console.error);
          audioContextRef.current = null;
        }
        stream.getTracks().forEach((track) => track.stop());

        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        const recordingDuration = startTimeRef.current
          ? (Date.now() - startTimeRef.current) / 1000
          : 0;
        startTimeRef.current = null;

        const shouldLogWarnings = debugMode || process.env.NODE_ENV !== "production";

        if (recordingDuration < 0.5) {
          if (shouldLogWarnings) {
            console.warn("MediaRecorder stopped immediately (Brave/Safari):", {
              duration: recordingDuration,
              pcmBuffers: pcmBuffers.length,
              samples: pcmBuffers[0]?.length || 0,
            });
          }
          onError?.(
            "Recording stopped unexpectedly. This may happen after network changes. Please try again."
          );
          onStateChange?.("idle");
          return;
        }

        if (debugMode) {
          console.log("Recording stopped - PCM from analyser:", {
            sampleRate,
            numChannels: pcmBuffers.length,
            samplesPerChannel: pcmBuffers[0]?.length || 0,
            durationSeconds: pcmBuffers[0]
              ? pcmBuffers[0].length / sampleRate
              : 0,
            maxAmplitude: maxAmplitude.toFixed(4),
            captureCount,
          });
        }

        if (
          pcmBuffers.length > 0 &&
          pcmBuffers[0].length > 0 &&
          maxAmplitude > 0.001
        ) {
          if (debugMode) {
            console.log("Using PCM captured from analyser (has audio)");
          }
          onPcmRecorded?.(pcmBuffers, sampleRate);
        } else {
          if (shouldLogWarnings) {
            console.warn(
              "Analyser PCM is empty/silent, trying MediaRecorder blob"
            );
          }
          const finalBlob = new Blob(audioChunks, {
            type: recorder.mimeType || preferredMime || "audio/webm",
          });

          if (finalBlob.size < 1000) {
            if (shouldLogWarnings) {
              console.warn("MediaRecorder blob is too small:", finalBlob.size);
            }
            onBlobRecorded?.(finalBlob);
            return;
          }

          // Try to decode the blob as fallback
          try {
            const AudioContextCtor2 =
              window.AudioContext ||
              (
                window as typeof window & {
                  webkitAudioContext?: typeof AudioContext;
                }
              ).webkitAudioContext;
            if (!AudioContextCtor2) {
              throw new Error("AudioContext not available");
            }

            const decodeContext = new AudioContextCtor2();
            const arrayBuffer = await finalBlob.arrayBuffer();
            let audioBuffer = await decodeContext.decodeAudioData(
              arrayBuffer.slice(0)
            );
            await decodeContext.close();

            // Trim AudioBuffer to exactly maxDurationSeconds
            const maxSamples = Math.floor(
              audioBuffer.sampleRate * maxDurationSeconds
            );
            if (audioBuffer.length > maxSamples) {
              const trimmedBuffer = decodeContext.createBuffer(
                audioBuffer.numberOfChannels,
                maxSamples,
                audioBuffer.sampleRate
              );
              for (
                let channel = 0;
                channel < audioBuffer.numberOfChannels;
                channel++
              ) {
                const channelData = audioBuffer.getChannelData(channel);
                trimmedBuffer.copyToChannel(
                  channelData.subarray(0, maxSamples),
                  channel
                );
              }
              audioBuffer = trimmedBuffer;
            }

            const decodedSampleRate = audioBuffer.sampleRate;
            const decodedChannels = audioBuffer.numberOfChannels;
            const decodedPcm: Float32Array[] = [];
            let decodedMaxAmplitude = 0;

            for (let channel = 0; channel < decodedChannels; channel++) {
              const channelData = audioBuffer.getChannelData(channel);
              decodedPcm.push(channelData);

              for (let i = 0; i < channelData.length; i++) {
                const abs = Math.abs(channelData[i]);
                if (abs > decodedMaxAmplitude) {
                  decodedMaxAmplitude = abs;
                }
              }
            }

            if (debugMode) {
              console.log("PCM from MediaRecorder decoded:", {
                sampleRate: decodedSampleRate,
                numChannels: decodedPcm.length,
                samplesPerChannel: decodedPcm[0]?.length || 0,
                durationSeconds: decodedPcm[0]
                  ? decodedPcm[0].length / decodedSampleRate
                  : 0,
                maxAmplitude: decodedMaxAmplitude.toFixed(4),
                blobSize: finalBlob.size,
              });
            }

            if (
              decodedPcm.length > 0 &&
              decodedPcm[0].length > 0 &&
              decodedMaxAmplitude > 0.001
            ) {
              onPcmRecorded?.(decodedPcm, decodedSampleRate);
            } else {
              console.warn(
                "Both analyser and MediaRecorder are silent, using blob directly"
              );
              onBlobRecorded?.(finalBlob);
            }
          } catch (err) {
            console.error("Error decoding final audio blob:", err);
            onBlobRecorded?.(finalBlob);
          }
        }
      };

      recorder.start(100);

      if (recorder.state !== "recording") {
        console.error(
          "MediaRecorder failed to start (Brave/Safari), state:",
          recorder.state
        );
        onError?.("Failed to start recording. Please try again.");
        stopStreams();
        onStateChange?.("idle");
        return false;
      }

      if (debugMode) {
        console.log(
          "MediaRecorder started successfully (Brave/Safari), state:",
          recorder.state
        );
      }
      return true;
    },
    [
      debugMode,
      maxDurationSeconds,
      onBlobRecorded,
      onError,
      onPcmRecorded,
      onStateChange,
      stopRecording,
      stopStreams,
    ]
  );

  /**
   * Handle fallback recording path (basic MediaRecorder).
   */
  const handleFallbackRecording = useCallback(
    (stream: MediaStream) => {
      usePcmCaptureRef.current = false;
      pcmStopHandlerRef.current = null;

      const preferredMime = pickSupportedMimeType();
      preferredMimeTypeRef.current = preferredMime;

      const recorder = preferredMime
        ? new MediaRecorder(stream, {
            mimeType: preferredMime,
            audioBitsPerSecond: 128000,
          })
        : new MediaRecorder(stream, { audioBitsPerSecond: 128000 });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      finalizeRef.current = false;

      const finalizeIfNeeded = () => {
        if (finalizeRef.current) return;
        finalizeRef.current = true;
        const firstChunk = chunksRef.current[0];
        const chunkType =
          firstChunk instanceof Blob ? firstChunk.type : undefined;
        const blob = new Blob(chunksRef.current, {
          type:
            recorder.mimeType ||
            chunkType ||
            preferredMimeTypeRef.current ||
            "audio/webm",
        });
        chunksRef.current = [];
        onBlobRecorded?.(blob);
      };

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }

        if (recorder.state === "inactive") {
          finalizeIfNeeded();
        }
      };

      recorder.onstop = () => {
        if (!finalizeRef.current) {
          setTimeout(finalizeIfNeeded, 0);
        }
      };

      recorder.start();

      if (recorder.state !== "recording") {
        console.error(
          "MediaRecorder failed to start (fallback path), state:",
          recorder.state
        );
        onError?.("Failed to start recording. Please try again.");
        stopStreams();
        onStateChange?.("idle");
        return false;
      }

      if (debugMode) {
        console.log(
          "MediaRecorder started successfully (fallback path), state:",
          recorder.state
        );
      }
      return true;
    },
    [debugMode, onBlobRecorded, onError, onStateChange, stopStreams]
  );

  /**
   * Start recording.
   */
  const startRecording = useCallback(async () => {
    try {
      // Check rate limit before starting (if callback provided)
      if (onBeforeStart) {
        try {
          const allowed = await onBeforeStart();
          if (allowed === false) {
            return;
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Rate limit check failed";
          if (onRateLimitError) {
            onRateLimitError(errorMessage);
          } else {
            onError?.(errorMessage);
          }
          return;
        }
      }

      // Clean up any stale MediaRecorder before starting
      if (mediaRecorderRef.current) {
        try {
          if (mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop();
          }
        } catch (e) {
          console.warn("Error cleaning up stale MediaRecorder:", e);
        }
        mediaRecorderRef.current = null;
      }

      // Clean up any stale AudioContext
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close().catch(console.error);
        audioContextRef.current = null;
      }

      const audioConstraints = getAudioConstraints();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });

      // Verify stream is active before proceeding
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        throw new Error("No audio tracks available in stream");
      }

      const firstTrack = audioTracks[0];
      if (firstTrack.readyState === "ended") {
        stream.getTracks().forEach((track) => track.stop());
        throw new Error("Audio track ended immediately. Please try again.");
      }

      const trackSettings = firstTrack?.getSettings();
      const chosenMimeType = preferredMimeTypeRef.current;

      if (debugMode) {
        console.log("=== Recording Start ===");
        console.log("Recording mode:", recordingMode);
        console.log("track.getSettings():", trackSettings);
        console.log("chosen mimeType:", chosenMimeType);
        console.log("Stream audio tracks:", {
          count: audioTracks.length,
          tracks: audioTracks.map((track) => ({
            id: track.id,
            label: track.label,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            settings: track.getSettings(),
            constraints: track.getConstraints(),
          })),
        });
      }

      // Check if any tracks are muted
      const mutedTracks = audioTracks.filter((track) => track.muted);
      if (mutedTracks.length > 0) {
        console.error("⚠️ MICROPHONE IS MUTED!", {
          mutedTracks: mutedTracks.length,
          totalTracks: audioTracks.length,
          trackDetails: audioTracks.map((t) => ({
            enabled: t.enabled,
            muted: t.muted,
            readyState: t.readyState,
            settings: t.getSettings(),
            capabilities: t.getCapabilities?.(),
          })),
          message:
            "The browser has microphone permission, but the track is muted by macOS.",
        });

        if (debugMode) {
          console.log("Attempting to diagnose mute issue:", {
            label: firstTrack.label,
            enabled: firstTrack.enabled,
            muted: firstTrack.muted,
            readyState: firstTrack.readyState,
            contentHint: firstTrack.contentHint,
          });
        }

        alert(
          "⚠️ Your microphone is muted\n\n" +
            "Please check your system's microphone settings:\n" +
            "• Is the correct microphone selected?\n" +
            "• Is the input volume above zero?\n" +
            "• Is the microphone physically muted?\n" +
            "• Are any other apps controlling the microphone?"
        );
        onStateChange?.("idle");
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      // Monitor track state changes
      audioTracks.forEach((track, index) => {
        track.addEventListener("mute", () => {
          console.warn(`Audio track ${index} was muted`);
        });
        track.addEventListener("unmute", () => {
          if (debugMode) {
            console.log(`Audio track ${index} was unmuted`);
          }
        });
        track.addEventListener("ended", () => {
          console.warn(`Audio track ${index} ended unexpectedly`);
          if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state === "recording"
          ) {
            console.warn(
              "Track ended during recording - stopping MediaRecorder"
            );
            try {
              mediaRecorderRef.current.stop();
            } catch (e) {
              console.error(
                "Error stopping MediaRecorder after track ended:",
                e
              );
            }
          }
        });
      });

      onStateChange?.("recording");
      setupTimer();

      // Choose recording path based on browser
      const useBraveSafari = isBraveOrSafari();

      if (!useBraveSafari) {
        // Chrome approach
        handleChromeRecording(stream);
      } else {
        // Brave/Safari approach
        await handleBraveSafariRecording(stream);
      }
    } catch (err) {
      console.error("Error in startRecording:", err);
      const errorMessage = err instanceof Error ? err.message : String(err);

      if ((err as DOMException).name === "NotAllowedError") {
        onError?.("Please enable microphone access to record a post.");
      } else if ((err as DOMException).name === "NotFoundError") {
        onError?.("No microphone found. Please check your device settings.");
      } else if ((err as DOMException).name === "NotReadableError") {
        onError?.(
          "Microphone is being used by another application. Please close it and try again."
        );
      } else if (errorMessage.includes("ended")) {
        onError?.("Audio track ended immediately. Please try again.");
      } else {
        onError?.("Microphone is unavailable. Please check your device.");
      }

      onStateChange?.("idle");
    }
  }, [
    debugMode,
    getAudioConstraints,
    handleBraveSafariRecording,
    handleChromeRecording,
    onBeforeStart,
    onError,
    onRateLimitError,
    onStateChange,
    recordingMode,
    setupTimer,
  ]);

  return {
    startRecording,
    stopRecording,
    stopStreams,
    getAudioConstraints,
    preferredMimeTypeRef,
    stoppedAtTimeLimitRef,
    getDefaultRecordingMode,
  };
}
